import type { CSSProperties } from 'react';
import { useDemo2Ctx } from '../Demo2Context';
import { TREES, STATUS_LABEL, STATUS_COLOR, MONITOR_LABEL, moistStatus, treeIconRi, type MoistStatus } from '../demo2Data';
import './Demo2Reports.css';

/* 월간 관개량 & 강수량 추이 — "유지·생장 호흡" 차트(레퍼런스)처럼 막대 없이
 * 얇은 듀얼 라인 + 좌우 축으로 구성한 미니멀한 스타일. 라인/그리드는 SVG로 100% 폭을
 * 채우고, 범례·축 라벨만 HTML 오버레이로 분리해 고정 px 폰트를 유지한다. */
function MonthlyTrendChart() {
    const months = ['2월', '3월', '4월', '5월', '6월', '7월'];
    const irr = [820, 910, 780, 860, 690, 730];
    const rain = [38, 62, 85, 52, 28, 18];
    const maxIrr = 1000, maxRain = 100;
    const VB = 1000;
    const n = months.length;
    const xMargin = 40;
    const xAt = (i: number) => xMargin + (i / (n - 1)) * (VB - xMargin * 2);
    const irrY = (v: number) => VB - (v / maxIrr) * VB;
    const rainY = (v: number) => VB - (v / maxRain) * VB;
    const irrPath = irr.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${irrY(v).toFixed(1)}`).join(' ');
    const rainPath = rain.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${rainY(v).toFixed(1)}`).join(' ');
    const irrTicks = [0, 250, 500, 750, 1000];
    const rainTicks = [0, 25, 50, 75, 100];

    return (
        <>
            <div className="report2-lc-legend">
                <span><span className="dot" style={{ background: '#1E8B53' }} />관개량 (백L)</span>
                <span><span className="dot dash" style={{ background: '#3B82F6' }} />강수량 (mm)</span>
            </div>
            <div className="report2-chart-box">
                <div className="report2-axis-y">
                    {irrTicks.slice().reverse().map(v => (
                        <span key={v} style={{ top: `${(irrY(v) / VB) * 100}%` }}>{v}</span>
                    ))}
                </div>
                <div className="report2-plot-area">
                    <svg viewBox={`0 0 ${VB} ${VB}`} preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block' }}>
                        {irrTicks.map(v => (
                            <line key={v} x1={0} y1={irrY(v)} x2={VB} y2={irrY(v)} stroke="#EEF1F0" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                        ))}
                        <path d={rainPath} fill="none" stroke="#3B82F6" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="14 10" vectorEffect="non-scaling-stroke" />
                        <path d={irrPath} fill="none" stroke="#1E8B53" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                    </svg>
                    {irr.map((v, i) => (
                        <span key={`i${i}`} className="report2-trend-dot" style={{ left: `${(xAt(i) / VB) * 100}%`, top: `${(irrY(v) / VB) * 100}%`, borderColor: '#1E8B53' }} />
                    ))}
                    {rain.map((v, i) => (
                        <span key={`r${i}`} className="report2-trend-dot" style={{ left: `${(xAt(i) / VB) * 100}%`, top: `${(rainY(v) / VB) * 100}%`, borderColor: '#3B82F6' }} />
                    ))}
                </div>
                <div className="report2-axis-y2">
                    {rainTicks.slice().reverse().map(v => (
                        <span key={v} style={{ top: `${(rainY(v) / VB) * 100}%` }}>{v}</span>
                    ))}
                </div>
                <div className="report2-axis-x">
                    {months.map((m, i) => {
                        const cls = i === 0 ? 'al' : i === n - 1 ? 'ar' : 'ac';
                        return <span key={m} className={cls} style={{ left: `${(xAt(i) / VB) * 100}%` }}>{m}</span>;
                    })}
                </div>
            </div>
        </>
    );
}

export default function Demo2Reports() {
    const { toast, zones } = useDemo2Ctx();
    const totalArea = zones.reduce((s, z) => s + z.area, 0);
    const totalTrees = zones.reduce((s, z) => s + z.treeCount, 0);
    const counts: Record<MoistStatus, number> = { good: 0, caution: 0, warn: 0, danger: 0 };
    zones.forEach(z => { counts[z.status]++; });
    const goodRate = Math.round((counts.good / zones.length) * 100);
    const avgMoist = Math.round(zones.reduce((s, z) => s + z.moist, 0) / zones.length);
    const urgentCnt = counts.warn + counts.danger;
    const drainageBad = zones.filter(z => z.drainage === '불량').length;
    const sensorCnt = zones.filter(z => z.monitorType === 'sensor').length;

    /* 색상톤은 레퍼런스(성장대시보드 "작물양분·질소" 도넛)의 청록·인디고·앰버·로즈 팔레트를 참고 */
    const segs: { k: MoistStatus; c: string }[] = [{ k: 'good', c: '#14B8A6' }, { k: 'caution', c: '#6366F1' }, { k: 'warn', c: '#F59E0B' }, { k: 'danger', c: '#F43F5E' }];
    let acc = 0;
    const stops = segs.map(s => {
        const pct = (counts[s.k] / zones.length) * 100;
        const stop = `${s.c} ${acc}% ${acc + pct}%`;
        acc += pct;
        return stop;
    });

    const speciesMap: Record<string, number> = {};
    TREES.forEach(t => { speciesMap[t.species] = (speciesMap[t.species] || 0) + 1; });
    const speciesArr = Object.entries(speciesMap).sort((a, b) => b[1] - a[1]);
    const maxS = speciesArr[0]?.[1] ?? 1;

    const sortedZ = [...zones].sort((a, b) => b.moist - a.moist);

    const KPI: { lbl: string; val: string; sub: string; ico: string; tone: string }[] = [
        { lbl: '등록 관리구역', val: `${zones.length}개`, sub: '공원·가로수·조경수', ico: 'ri-building-3-line', tone: 'blue' },
        { lbl: '총 관리 면적', val: `${(totalArea / 10000).toFixed(1)}ha`, sub: `${totalArea.toLocaleString()}㎡`, ico: 'ri-shape-line', tone: 'green' },
        { lbl: '총 식재 수목', val: `${totalTrees.toLocaleString()}주`, sub: '전체 등록 수종', ico: 'ri-tree-line', tone: 'green' },
        { lbl: '수분 양호율', val: `${goodRate}%`, sub: `${counts.good}/${zones.length}개 구역 양호`, ico: 'ri-drop-line', tone: 'teal' },
        { lbl: '관개 필요 구역', val: `${urgentCnt}개`, sub: '위험·심각 구역', ico: 'ri-alarm-warning-line', tone: 'red' },
        { lbl: '평균 수분율', val: `${avgMoist}%`, sub: '전 구역 평균', ico: 'ri-water-percent-line', tone: moistStatus(avgMoist) === 'good' ? 'green' : moistStatus(avgMoist) === 'caution' ? 'blue' : moistStatus(avgMoist) === 'warn' ? 'orange' : 'red' },
    ];

    const mgmtRows: { lbl: string; val: string; ico: string; ok: boolean; note: string }[] = [
        { lbl: '평균 토양 pH', val: (zones.reduce((s, z) => s + z.ph, 0) / zones.length).toFixed(1), ico: 'ri-flask-line', ok: true, note: '적정 범위 6.0~7.0' },
        { lbl: '배수 불량 구역', val: `${drainageBad}개`, ico: 'ri-alarm-warning-line', ok: drainageBad === 0, note: '즉각 개선 필요' },
        { lbl: '센서 기반 구역', val: `${sensorCnt}개`, ico: 'ri-radar-line', ok: true, note: '라이시미터 센서 운영' },
        { lbl: '센서리스 AI예측 구역', val: `${zones.length - sensorCnt}개`, ico: 'ri-brain-line', ok: true, note: '위성·기상데이터 기반' },
        { lbl: '담당자 배정률', val: '100%', ico: 'ri-user-3-line', ok: true, note: '전담 관리 현황' },
        { lbl: '긴급 관개 필요', val: `${counts.danger}개 구역`, ico: 'ri-drop-line', ok: counts.danger === 0, note: '당일 처리 권고' },
    ];

    const AI = [
        { ico: 'ri-drop-line', col: '#2563EB', bg: '#EFF6FF', bd: '#BFDBFE', title: '관개 효율 분석', body: `전체 ${zones.length}개 구역 중 ${urgentCnt}개 구역이 즉각 관개가 필요합니다. 수분율 25% 미만 ${counts.danger}개 구역은 오늘 중 관개 처리를 권장드립니다. 스마트 스케줄링 적용 시 관개량 최대 15% 절감이 가능합니다.` },
        { ico: 'ri-sun-line', col: '#D97706', bg: '#FFFBEB', bd: '#FDE68A', title: '기후 영향 평가', body: `이번주 강수량이 평년 대비 부족합니다. 특히 배수 불량 ${drainageBad}개 구역은 관개 효과가 제한적이므로 배수 개선 공사를 병행할 것을 권장합니다.` },
        { ico: 'ri-leaf-line', col: '#059669', bg: '#ECFDF5', bd: '#6EE7B7', title: '수종별 관리 권고', body: '왕벚나무·이팝나무 군락은 여름철 폭염기 집중 관개가 필요합니다. 메타세쿼이아 구역은 뿌리 깊이까지 충분히 적시는 심층 관개를 권장하며, 소나무·잣나무 구역은 과습에 주의해 배수 관리에 중점을 두세요.' },
        { ico: 'ri-line-chart-line', col: '#7C3AED', bg: '#F5F3FF', bd: '#C4B5FD', title: '8월 예측 & 계획', body: `8월 예상 강수량은 평년보다 낮을 전망입니다. 배수 불량 ${drainageBad}개 구역에 대한 선제적 배수 개선 공사를 권장하며, 센서리스 구역은 위성 영상 기반 예측 정확도를 지속 검증하세요.` },
    ];

    return (
        <div className="report2">
            <div className="report2-toolbar">
                <div className="report2-toolbar-ttl"><i className="ri-bar-chart-box-line" /> 종합 리포트</div>
                <div style={{ flex: 1 }} />
                <select className="report2-select"><option>2026년 7월</option><option>2026년 6월</option><option>2026년 5월</option></select>
                <select className="report2-select"><option>전체 관리구역</option></select>
                <button className="report2-btn" onClick={() => toast('PDF 생성', 'success')}><i className="ri-file-text-line" /> PDF</button>
                <button className="report2-btn" onClick={() => toast('Excel 내보내기', 'success')}><i className="ri-download-2-line" /> Excel</button>
                <button className="report2-btn report2-btn-primary" onClick={() => window.print()}><i className="ri-printer-line" /> 인쇄</button>
            </div>

            <div className="report2-body">
                <div className="report2-hero">
                    <div className="report2-hero-pattern" />
                    <div className="report2-hero-left">
                        <div className="report2-hero-kicker"><i className="ri-shield-check-line" /> AIS × 수프로 · LH TREE MANAGEMENT REPORT</div>
                        <div className="report2-hero-ttl">수목관리 통합 현황 보고서</div>
                        <div className="report2-hero-meta">
                            <span className="report2-hero-chip"><i className="ri-calendar-line" /> 2026년 7월 기준</span>
                            <span className="report2-hero-chip"><i className="ri-file-list-3-line" /> 2026.07.20 발행</span>
                            <span className="report2-hero-chip report2-hero-chip-live"><i className="ri-broadcast-line" /> 실시간 데이터 연동</span>
                        </div>
                    </div>
                    <div className="report2-hero-stats">
                        <div className="report2-hero-stat">
                            <div className="report2-hero-stat-ico blue"><i className="ri-map-pin-line" /></div>
                            <div><div className="report2-hero-stat-val">{zones.length}<span>개</span></div><div className="report2-hero-stat-lbl">관리구역</div></div>
                        </div>
                        <div className="report2-hero-stat">
                            <div className="report2-hero-stat-ico green"><i className="ri-shape-line" /></div>
                            <div><div className="report2-hero-stat-val">{(totalArea / 10000).toFixed(1)}<span>ha</span></div><div className="report2-hero-stat-lbl">총 관리면적</div></div>
                        </div>
                        <div className="report2-hero-stat">
                            <div className="report2-hero-stat-ico teal"><i className="ri-water-percent-line" /></div>
                            <div><div className="report2-hero-stat-val">{avgMoist}<span>%</span></div><div className="report2-hero-stat-lbl">평균 수분율</div></div>
                        </div>
                    </div>
                </div>

                <div className="report2-kpi-grid">
                    {KPI.map((k, i) => (
                        <div key={i} className="report2-kpi-card">
                            <div className="report2-kpi-top">
                                <div className={`d2-ico d2-ico-md d2-ico-${k.tone}`}><i className={k.ico} /></div>
                                <span className="report2-kpi-lbl">{k.lbl}</span>
                            </div>
                            <div className="report2-kpi-val">{k.val}</div>
                            <div className="report2-kpi-sub">{k.sub}</div>
                        </div>
                    ))}
                </div>

                <div className="report2-row-top">
                    <div className="report2-card">
                        <div className="report2-card-hd"><span className="report2-card-hd-ttl">수분 상태 분포</span></div>
                        <div className="report2-card-body">
                            <div className="report2-donut-wrap">
                                <div style={{ position: 'relative', width: 168, height: 168 }}>
                                    <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: `conic-gradient(${stops.join(',')})` }} />
                                    <div style={{ position: 'absolute', inset: 26, background: '#fff', borderRadius: '50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', boxShadow: 'inset 0 0 0 1px var(--d-line)' }}>
                                        <div style={{ fontSize: 22, fontWeight: 900, color: 'var(--d-ink)' }}>{zones.length}</div>
                                        <div style={{ fontSize: 10, color: 'var(--d-ink3)' }}>전체 구역</div>
                                    </div>
                                </div>
                            </div>
                            <div className="report2-donut-legend">
                                {segs.map(s => (
                                    <div key={s.k} className="report2-donut-legend-item" style={{ '--lg-bg': `${s.c}12`, '--lg-color': s.c } as CSSProperties}>
                                        <span className="report2-donut-legend-dot" />
                                        <div>
                                            <div className="report2-donut-legend-lbl">{STATUS_LABEL[s.k]}</div>
                                            <div className="report2-donut-legend-val">{counts[s.k]}<span style={{ fontSize: 10, fontWeight: 600 }}>개</span></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="report2-card">
                        <div className="report2-card-hd">
                            <span className="report2-card-hd-ttl">월간 관개량 &amp; 강수량 추이</span>
                            <span className="report2-pill" style={{ background: '#D1FAE5', color: '#166534' }}>전월比 −6.2% ↓</span>
                        </div>
                        <div style={{ paddingTop: 14 }}>
                            <MonthlyTrendChart />
                        </div>
                    </div>
                </div>

                <div className="report2-card">
                    <div className="report2-card-hd"><span className="report2-card-hd-ttl">구역별 현재 수분율 현황</span></div>
                    <div className="report2-card-body">
                        <div className="report2-bar-rows">
                            {sortedZ.map(z => (
                                <div key={z.id} className="report2-bar-row">
                                    <div className="report2-bar-row-name"><i className={treeIconRi(z.treeName)} /> {z.name}</div>
                                    <div className="report2-bar-row-track"><div className="report2-bar-row-fill" style={{ width: `${z.moist}%`, background: STATUS_COLOR[z.status] }} /></div>
                                    <div className="report2-bar-row-val" style={{ color: STATUS_COLOR[z.status] }}>{z.moist}%</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="report2-row-top" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <div className="report2-card">
                        <div className="report2-card-hd"><span className="report2-card-hd-ttl">수종 분포 현황</span></div>
                        <div className="report2-card-body">
                            {speciesArr.map(([name, cnt]) => {
                                const pct = Math.round((cnt / maxS) * 100);
                                return (
                                    <div key={name} className="report2-species-row">
                                        <div className="report2-species-top">
                                            <span className="report2-species-ico"><i className={treeIconRi(name)} /></span>
                                            <span className="report2-species-name">{name}</span>
                                            <span className="report2-species-cnt">{cnt}주(샘플)</span>
                                        </div>
                                        <div className="report2-species-track"><div className="report2-species-fill" style={{ width: `${pct}%` }} /></div>
                                    </div>
                                );
                            })}
                            <div className="text-xs text-muted mt-2">※ 개체목 표본 데이터 기준 (전체 {totalTrees.toLocaleString()}주 중 샘플)</div>
                        </div>
                    </div>

                    <div className="report2-card">
                        <div className="report2-card-hd"><span className="report2-card-hd-ttl">토양 및 관리 지표</span></div>
                        <div className="report2-card-body">
                            {mgmtRows.map(r => (
                                <div key={r.lbl} className="report2-mgmt-row" style={{ '--mg-bg': r.ok ? '#F0FDF4' : '#FFF1F2', '--mg-bd': r.ok ? '#BBF7D0' : '#FECDD3', '--mg-tone': r.ok ? '#166534' : '#DC2626' } as CSSProperties}>
                                    <span className="report2-mgmt-ico"><i className={r.ico} /></span>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="report2-mgmt-lbl">{r.lbl}</div>
                                        <div className="report2-mgmt-val">{r.val}</div>
                                    </div>
                                    <div className="report2-mgmt-note">{r.note}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="report2-card">
                    <div className="report2-card-hd"><span className="report2-card-hd-ttl">전체 관리구역 상세 현황</span></div>
                    <div className="report2-tbl-wrap">
                        <table className="report2-tbl">
                            <thead><tr><th>#</th><th>상태</th><th>구역명</th><th>구분</th><th>모니터링</th><th>주요 수종</th><th>그루수</th><th>면적(㎡)</th><th style={{ minWidth: 120 }}>수분율</th><th>토양pH</th><th>배수</th><th>담당자</th><th>최근 관개</th></tr></thead>
                            <tbody>
                                {zones.map((z, i) => (
                                    <tr key={z.id}>
                                        <td className="text-muted">{i + 1}</td>
                                        <td><span className={`badge badge-${z.status}`}><span className={`badge-dot dot-${z.status}`} />{STATUS_LABEL[z.status]}</span></td>
                                        <td className="bold"><i className={treeIconRi(z.treeName)} /> {z.name}</td><td>{z.category}</td>
                                        <td><span className="badge badge-neutral">{MONITOR_LABEL[z.monitorType]}</span></td>
                                        <td>{z.treeName}</td>
                                        <td className="text-mono">{z.treeCount}주</td><td className="text-mono">{z.area.toLocaleString()}</td>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                                <div style={{ flex: 1, height: 5, background: '#E5F0E5', borderRadius: 3, overflow: 'hidden', minWidth: 50 }}><div style={{ width: `${z.moist}%`, height: '100%', background: STATUS_COLOR[z.status], borderRadius: 3 }} /></div>
                                                <span style={{ color: STATUS_COLOR[z.status], fontWeight: 800, fontSize: 11 }}>{z.moist}%</span>
                                            </div>
                                        </td>
                                        <td className="text-mono">{z.ph}</td>
                                        <td><span style={{ fontSize: 11, fontWeight: 700, borderRadius: 5, padding: '2px 7px', background: z.drainage === '불량' ? '#FEE2E2' : '#F0FDF4', color: z.drainage === '불량' ? '#DC2626' : '#166534' }}>{z.drainage}</span></td>
                                        <td>{z.mgr}</td><td className="text-mono text-xs">{z.lastDate}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="report2-card" style={{ marginBottom: 4 }}>
                    <div className="report2-card-hd">
                        <span className="report2-card-hd-ttl"><i className="ri-brain-line" /> AI 종합 인사이트 &amp; 권고사항</span>
                        <span className="report2-pill" style={{ background: '#EDE9FE', color: '#7C3AED' }}>AI 자동 분석</span>
                    </div>
                    <div className="report2-card-body">
                        <div className="report2-ai-grid">
                            {AI.map(a => (
                                <div key={a.title} className="report2-ai-card" style={{ '--ai-bg': a.bg, '--ai-bd': a.bd, '--ai-col': a.col } as CSSProperties}>
                                    <div className="report2-ai-top">
                                        <span className="report2-ai-ico"><i className={a.ico} /></span>
                                        <span className="report2-ai-title">{a.title}</span>
                                    </div>
                                    <p className="report2-ai-body">{a.body}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="report2-ai-footer">
                        <i className="ri-information-line" /><span>본 리포트는 실시간 관리구역 데이터를 기반으로 AI가 자동 생성했습니다.</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
